'use client';

import { useEffect, useRef, useCallback, useState } from 'react';

interface UseSSEOptions {
  url?: string;
  enabled?: boolean;
  onCall?: (data: unknown) => void;
  onOperator?: (data: unknown) => void;
  onActivity?: (data: unknown) => void;
  onMetrics?: (data: unknown) => void;
  onShiftSwap?: (data: unknown) => void;
  onCallback?: (data: unknown) => void;
  onChat?: (data: unknown) => void;
  onBroadcast?: (data: unknown) => void;
  onAnnouncement?: (data: unknown) => void;
  onChatReaction?: (data: unknown) => void;
  onNotification?: (data: unknown) => void;
  onCallState?: (data: unknown) => void;
}

interface SSEState {
  connected: boolean;
  reconnectCount: number;
  lastEventAt: string | null;
  error: string | null;
}

const MIN_RECONNECT_MS = 1000;
const MAX_RECONNECT_MS = 30000;
const JITTER_MS = 500;

export function useSSE(options: UseSSEOptions = {}): SSEState {
  const {
    url = '/api/sse',
    enabled = true,
    onCall,
    onOperator,
    onActivity,
    onMetrics,
    onShiftSwap,
    onCallback,
    onChat,
    onBroadcast,
    onAnnouncement,
    onChatReaction,
    onNotification,
    onCallState,
  } = options;

  const [state, setState] = useState<SSEState>({
    connected: false,
    reconnectCount: 0,
    lastEventAt: null,
    error: null,
  });

  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectCountRef = useRef(0);
  const mountedRef = useRef(true);

  const getReconnectDelay = useCallback((count: number) => {
    const base = Math.min(MIN_RECONNECT_MS * Math.pow(2, count), MAX_RECONNECT_MS);
    const jitter = Math.random() * JITTER_MS;
    return base + jitter;
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled) return;

    // Close existing connection
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    const es = new EventSource(url);
    esRef.current = es;

    es.onopen = () => {
      if (!mountedRef.current) return;
      reconnectCountRef.current = 0;
      setState(prev => ({ ...prev, connected: true, reconnectCount: 0, error: null }));
    };

    es.onerror = () => {
      if (!mountedRef.current) return;
      es.close();
      esRef.current = null;
      setState(prev => ({ ...prev, connected: false, error: 'Connessione SSE persa' }));

      const delay = getReconnectDelay(reconnectCountRef.current);
      reconnectCountRef.current += 1;
      setState(prev => ({ ...prev, reconnectCount: reconnectCountRef.current }));

      reconnectTimerRef.current = setTimeout(() => {
        if (mountedRef.current) connect();
      }, delay);
    };

    const handleEvent = (eventName: string, handler?: (data: unknown) => void) => {
      es.addEventListener(eventName, (e: MessageEvent) => {
        if (!mountedRef.current) return;
        setState(prev => ({ ...prev, lastEventAt: new Date().toISOString() }));
        try {
          const data = JSON.parse(e.data);
          handler?.(data);
        } catch {
          handler?.(e.data);
        }
      });
    };

    handleEvent('call', onCall);
    handleEvent('operator', onOperator);
    handleEvent('activity', onActivity);
    handleEvent('metrics', onMetrics);
    handleEvent('shiftSwap', onShiftSwap);
    handleEvent('callback', onCallback);
    handleEvent('chat', onChat);
    handleEvent('broadcast', onBroadcast);
    handleEvent('announcement', onAnnouncement);
    handleEvent('chatReaction', onChatReaction);
    handleEvent('notification', onNotification);
    handleEvent('callState', onCallState);

    // Generic message handler
    es.onmessage = (e: MessageEvent) => {
      if (!mountedRef.current) return;
      setState(prev => ({ ...prev, lastEventAt: new Date().toISOString() }));
    };
  }, [url, enabled, onCall, onOperator, onActivity, onMetrics, onShiftSwap, onCallback, onChat, onBroadcast, onAnnouncement, onChatReaction, onNotification, onCallState, getReconnectDelay]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [connect, enabled]);

  return state;
}
